import { makeAutoObservable } from 'mobx'

export class Loader<RequestType = void, ResponseType = void> {
  loading = false

  #abortController = new AbortController()

  startLoading() {
    this.loading = true
  }

  stopLoading() {
    if (this._counterCalls === 0) {
      this.loading = false
    }
  }

  withDebounce = false
  debounceDelay = 800

  // Настройки повторных запросов
  retryCount = 3
  retryDelay = 10_000 // 10 секунд
  withRetry = false
  shouldRetry: (error: any) => boolean = () => true

  constructor(
    private _request: (
      params: RequestType,
      options: { abortSignal: AbortSignal }
    ) => Promise<ResponseType>,
    options: {
      withDebounce?: boolean
      debounceDelay?: number
      isLoading?: boolean
      withRetry?: boolean
      retryCount?: number
      retryDelay?: number
      shouldRetry?: (error: any) => boolean
    } = {}
  ) {
    this.withDebounce = options.withDebounce ?? this.withDebounce
    this.debounceDelay = options.debounceDelay ?? this.debounceDelay
    this.loading = options.isLoading ?? false

    this.withRetry = options.withRetry ?? this.withRetry
    this.retryCount = options.retryCount ?? this.retryCount
    this.retryDelay = options.retryDelay ?? this.retryDelay
    this.shouldRetry = options.shouldRetry ?? this.shouldRetry

    makeAutoObservable(this, {}, { autoBind: true })
  }

  async request(props: RequestType) {
    if (this.withDebounce) {
      this.executeRequestWithDebounce(props)
    } else {
      return await this.requestWithoutDebounce(props)
    }
  }

  /**
   * Выполняет запрос сразу, игнорируя настройку withDebounce
   */
  async requestWithoutDebounce(props: RequestType) {
    this.startLoading()
    try {
      if (this.withRetry) {
        return await this.executeRequestWithRetry(props, 0)
      } else {
        return await this._request(props, {
          abortSignal: this.#abortController.signal,
        })
      }
    } catch (error) {
      throw error
    } finally {
      this.stopLoading()
    }
  }

  private _debounceTimerId: ReturnType<typeof setTimeout> | null = null
  private _counterCalls = 0

  private executeRequestWithDebounce(props: RequestType) {
    this.startLoading()
    if (this._debounceTimerId != null) {
      clearTimeout(this._debounceTimerId)
    }
    return new Promise<ResponseType>((resolve) => {
      this._debounceTimerId = setTimeout(async () => {
        this._debounceTimerId = null
        try {
          this._counterCalls++
          if (this.withRetry) {
            const response = await this.executeRequestWithRetry(props, 0)
            resolve(response)
          } else {
            const response = await this._request(props, {
              abortSignal: this.#abortController.signal,
            })
            resolve(response)
          }
        } catch (error) {
          throw error
        } finally {
          this._counterCalls--
          this.stopLoading()
        }
      }, this.debounceDelay)
    })
  }

  private _requestRetryTimerId: ReturnType<typeof setTimeout> | null = null
  private async executeRequestWithRetry(props: RequestType, retryAttempt = 0) {
    try {
      return await this._request(props, {
        abortSignal: this.#abortController.signal,
      })
    } catch (error) {
      if (retryAttempt < this.retryCount && this.shouldRetry(error)) {
        return await new Promise<ResponseType>((resolve) => {
          this._requestRetryTimerId = setTimeout(async () => {
            const response = await this.executeRequestWithRetry(
              props,
              retryAttempt + 1
            )
            resolve(response)
          }, this.retryDelay)
        })
      }

      throw error
    }
  }

  cancel() {
    this.#abortController.abort()
    this.#abortController = new AbortController()

    if (this._debounceTimerId !== null) {
      clearTimeout(this._debounceTimerId)
      this._debounceTimerId = null
    }

    if (this._requestRetryTimerId !== null) {
      clearTimeout(this._requestRetryTimerId)
      this._requestRetryTimerId = null
    }

    this._counterCalls = 0
    this.stopLoading()
  }
}
